/**
 * Tab Specialità nell'Admin Panel.
 * CRUD specialità veterinarie.
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { specialtiesApi, Specialty, SpecialtyCreate } from '../../api/specialties'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

export function SpecialtiesTab() {
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState<SpecialtyCreate>({ key: '', label: '', description: '' })

  const queryClient = useQueryClient()

  const { data: specialties } = useQuery<Specialty[]>({
    queryKey: ['specialties'],
    queryFn: () => specialtiesApi.list(),
  })

  const createMutation = useMutation({
    mutationFn: specialtiesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specialties'] })
      queryClient.invalidateQueries({ queryKey: ['specialties-active'] })
      setShowForm(false)
      setForm({ key: '', label: '', description: '' })
    },
    onError: (error: any) => {
      alert(error?.response?.data?.detail || 'Errore durante la creazione')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ key, data }: { key: string; data: { label?: string; description?: string } }) =>
      specialtiesApi.update(key, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specialties'] })
      queryClient.invalidateQueries({ queryKey: ['specialties-active'] })
    },
    onError: (error: any) => {
      alert(error?.response?.data?.detail || 'Errore durante l\'aggiornamento')
    },
  })

  const deactivateMutation = useMutation({
    mutationFn: specialtiesApi.deactivate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specialties'] })
      queryClient.invalidateQueries({ queryKey: ['specialties-active'] })
    },
    onError: (error: any) => {
      alert(error?.response?.data?.detail || 'Errore durante la disattivazione')
    },
  })

  const activateMutation = useMutation({
    mutationFn: specialtiesApi.activate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specialties'] })
      queryClient.invalidateQueries({ queryKey: ['specialties-active'] })
    },
    onError: (error: any) => {
      alert(error?.response?.data?.detail || 'Errore durante la riattivazione')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    createMutation.mutate(form)
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground">Specialità Veterinarie</h2>
        <Button onClick={() => setShowForm(!showForm)}>
          + Aggiungi Specialità
        </Button>
      </div>

      {showForm && (
        <Card className="mb-6">
          <CardContent className="p-6">
            <h3 className="text-md font-semibold text-foreground mb-4">Nuova Specialità</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Chiave</Label>
                  <Input
                    value={form.key}
                    onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                    placeholder="nuova_specialita"
                    required
                  />
                </div>
                <div>
                  <Label>Etichetta</Label>
                  <Input
                    value={form.label}
                    onChange={(e) => setForm({ ...form, label: e.target.value })}
                    placeholder="Nuova specialità"
                    required
                  />
                </div>
                <div>
                  <Label>Descrizione</Label>
                  <Input
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="sintomi, condizioni..."
                    required
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Salvataggio...' : 'Salva'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Annulla
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Chiave</TableHead>
              <TableHead>Etichetta</TableHead>
              <TableHead>Descrizione</TableHead>
              <TableHead>Stato</TableHead>
              <TableHead>Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {specialties?.map((spec) => (
              <TableRow key={spec.key}>
                <TableCell className="font-mono">{spec.key}</TableCell>
                <TableCell>
                  <Input
                    value={spec.label}
                    onChange={(e) => {
                      updateMutation.mutate({ key: spec.key, data: { label: e.target.value } })
                    }}
                    className="w-full"
                  />
                </TableCell>
                <TableCell>
                  <Input
                    value={spec.description}
                    onChange={(e) => {
                      updateMutation.mutate({ key: spec.key, data: { description: e.target.value } })
                    }}
                    className="w-full"
                  />
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={
                    spec.is_active
                      ? 'bg-success/10 text-success border-success/20'
                      : 'bg-muted text-muted-foreground border-border'
                  }>
                    {spec.is_active ? 'Attivo' : 'Disattivato'}
                  </Badge>
                </TableCell>
                <TableCell>
                  {spec.is_active ? (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => deactivateMutation.mutate(spec.key)}
                    >
                      Disattiva
                    </Button>
                  ) : (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => activateMutation.mutate(spec.key)}
                    >
                      Riattiva
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
